/**
 * Panneau lateral (tiroir) : filtres avances, navigation mobile. Meme contrat
 * clavier qu'une modale ; il glisse depuis le bord choisi.
 */
import { useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { BoutonIcone } from './boutons';
import { useCoucheModale } from './couche';
import type { OptionsFocusCouche } from './couche';

export function PanneauLateral({
  ouvert,
  onFermer,
  titre,
  description,
  children,
  pied,
  cote = 'droite',
  largeur = 'max-w-md',
  verrouillee = false,
  refFocusInitial,
  refFocusRetour,
}: OptionsFocusCouche & {
  ouvert: boolean;
  onFermer: () => void;
  titre: string;
  description?: ReactNode;
  children: ReactNode;
  pied?: ReactNode;
  cote?: 'droite' | 'gauche';
  largeur?: string;
  /**
   * Retire toutes les sorties implicites (croix, Echap, clic sur le fond),
   * comme `Modale` : pendant une ecriture, fermer ferait perdre sa
   * confirmation ou son echec. La seule issue est une action du pied.
   */
  verrouillee?: boolean;
}) {
  const refPanneau = useRef<HTMLDivElement>(null);
  const idTitre = useId();
  const idDescription = useId();

  useCoucheModale({ ouverte: ouvert, refConteneur: refPanneau, onEchap: verrouillee ? undefined : onFermer, refFocusInitial, refFocusRetour });

  if (!ouvert) return null;

  return createPortal(
    <div className={`fixed inset-0 z-50 flex ${cote === 'droite' ? 'justify-end' : 'justify-start'}`}>
      <div aria-hidden="true" className="fixed inset-0 animate-fondu bg-neutre-950/40" onClick={verrouillee ? undefined : onFermer} />
      <div
        ref={refPanneau}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitre}
        aria-describedby={description ? idDescription : undefined}
        tabIndex={-1}
        className={`relative z-10 flex h-full w-full ${largeur} flex-col border-bordure bg-surface shadow-flottant ${
          cote === 'droite' ? 'animate-glisser-depuis-droite border-l' : 'animate-glisser-depuis-gauche border-r'
        }`}
      >
        <header className="flex items-start justify-between gap-3 border-b border-bordure px-5 py-4">
          <div className="min-w-0">
            <h2 id={idTitre} className="text-md font-semibold tracking-titre text-encre">
              {titre}
            </h2>
            {description && (
              <div id={idDescription} className="mt-0.5 text-sm text-encre-discrete">
                {description}
              </div>
            )}
          </div>
          {!verrouillee && <BoutonIcone libelle="Fermer le panneau" icone={X} onClick={onFermer} className="-mt-1 -mr-2 shrink-0" />}
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {pied && (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-bordure bg-surface-douce px-5 py-3">
            {pied}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
